import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { MapProperties } from '@datum-map';
import { Feature } from 'ol';
import ClusterSource from 'ol/source/Cluster';
import { AnimatedClusterLayerBuilder } from '@datum-group/datum-map/lib';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import { Point } from 'ol/geom';
import { bbox } from 'ol/loadingstrategy';
import image from '@assets/images/news-layer.svg';
import { FilterParam, FilterSearchGroups } from '../Query/Filter';
import {
  FeatureCollectionSourceLoaderDecorator,
  LoadFeatureCollectionCallback,
} from '../SourceLoaderDecorator';
import { INewsLayerController } from './types';
import { DATUM_MAP_CONFIG } from '../datumMapConfig';

interface NewsLayerQuery {
  filter: FilterParam[][];
  search?: string;
}

export class NewsMapWidgetLayerController implements INewsLayerController {
  readonly layer: VectorLayer;
  readonly source: VectorSource<Feature<Point>>;

  private _query?: NewsLayerQuery;
  private _mapProperties: MapProperties;

  @observable private _isVisible = false;
  @observable private _count = undefined;
  @observable private _requestsInProgress: number = 0;
  @observable private _error: string | undefined = undefined;

  constructor(
    loadFeatures: LoadFeatureCollectionCallback,
    mapProperties: MapProperties,
  ) {
    this._mapProperties = mapProperties;

    this.source = new VectorSource({ strategy: bbox });
    this.layer = this._buildLayer();

    new FeatureCollectionSourceLoaderDecorator(
      this.source,
      mapProperties.map,
      async (config) => {
        const params = new URLSearchParams(config.params.toString());
        const extra = this._buildExtraParams();

        if (extra) {
          extra.forEach((value, key) => {
            params.set(key, value);
          });
        }

        return await loadFeatures({ ...config });
      },
    );

    this._bind();

    makeObservable<NewsMapWidgetLayerController>(this);
  }

  // TODO: Всегда равен 0. Эндпоинт open-news-geojson/ не возврашает count
  @computed get count() {
    return this._count;
  }

  // TODO: Всегда равно undefined.
  // Надо договориться об отображении ошибки, в том числе при условии частого изменения bbox. (10 запросов подряд => 9 первых кенселится => интерфейс моргает)
  @computed get error(): string | undefined {
    return this._error;
  }

  @computed get isLoading(): boolean {
    return this._requestsInProgress > 0;
  }

  @computed get isVisible() {
    return this._isVisible;
  }

  @action show = () => {
    if (!this._isVisible) {
      this._mapProperties.map.addLayer(this.layer);
    }

    this._isVisible = true;
  };

  @action hide = () => {
    this._isVisible = false;
    this._mapProperties.map.removeLayer(this.layer);
  };

  get query(): NewsLayerQuery | undefined {
    return this._query;
  }

  set query(query: NewsLayerQuery | undefined) {
    this._query = query;
  }

  private _bind = () => {
    this.source.on('featuresloadstart', () => {
      runInAction(() => this._requestsInProgress++);
    });

    this.source.on(['featuresloadend', 'featuresloaderror'], () => {
      runInAction(() => this._requestsInProgress--);
    });
  };

  private _buildLayer = () => {
    const emptyStyle = new Style({});
    const singleFeatureStyle = new Style({
      image: new Icon({ src: image, anchor: [0.5, 0.5], scale: 0.03 }),
    });
    const clusterStyle = new Style({
      image: new Icon({ src: image, anchor: [0.5, 0.5], scale: 0.04 }),
    });

    const builder = new AnimatedClusterLayerBuilder(
      DATUM_MAP_CONFIG.map.layer.cluster,
    );

    const { layer } = builder
      .setMap(this._mapProperties.map)
      .setSource(new ClusterSource({ distance: 120, source: this.source }))
      .setStyle({
        clusterStyle: clusterStyle,
        featureStyle: [singleFeatureStyle],
        activeClusterStyle: emptyStyle,
        activeFeatureStyle: singleFeatureStyle,
      })
      .build();

    layer.setZIndex(999);

    return layer;
  };

  private _buildExtraParams = () => {
    if (!this.query) return undefined;

    const { filter, search } = this.query;

    const filterSearchParams = new FilterSearchGroups(filter).toString();
    const params = new URLSearchParams(filterSearchParams);

    if (search) params.set('_search', search);

    return params;
  };
}
